import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { Building2, Lock, Beaker, Shield, ArrowRight, Clock, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "/amidex-logo.png";

/**
 * B2B Auth Gate Component
 * Blocks access to protected routes (products, checkout, etc.) for:
 * - Non-authenticated users
 * - PENDING status users (waiting for approval)
 * - REJECTED status users
 * 
 * Shows a professional B2B landing page prompting login/registration.
 */
const AuthGate = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Verifying credentials...</p>
        </div>
      </div>
    );
  }

  // If authenticated, check account status
  if (isAuthenticated && user) {
    const accountStatus = user.account_status || 'APPROVED'; // Legacy users default to APPROVED
    
    // PENDING users - redirect to pending page
    if (accountStatus === 'PENDING') {
      return <Navigate to="/account/pending" replace />;
    }
    
    // REJECTED users - redirect to rejected page  
    if (accountStatus === 'REJECTED') {
      return <Navigate to="/account/rejected" replace />;
    }
    
    // APPROVED users - grant access
    return children;
  }

  // Not authenticated - show B2B gate
  return (
    <div className="min-h-screen bg-slate-50" data-testid="auth-gate">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/">
            <img src={LOGO_URL} alt="Amidex" className="h-8 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            <a 
              href="/login" 
              className="text-sm text-slate-600 hover:text-slate-900 font-medium"
            >
              Sign In
            </a>
            <a 
              href="/register"
              className="text-sm bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 font-medium"
            >
              Apply for Access
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-2xl mb-6">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading font-bold text-4xl text-slate-900 mb-4">
            Institutional Access Required
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Product information is available to approved institutional accounts only. 
            Please register or log in to continue.
          </p>
        </div>

        {/* B2B Notice Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800">
              <strong>Institutional Accounts Only:</strong> Registration is restricted to verified 
              institutional email addresses (<strong>.edu</strong>, <strong>.org</strong>, <strong>.gov</strong>). 
              All applications are manually reviewed within 48 hours.
            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-2xl p-6 border border-slate-200">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Who We Serve</h3>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>• Academic Research Laboratories</li>
              <li>• Government Research Agencies</li>
              <li>• Non-Profit Research Institutions</li>
              <li>• Compliance-reviewed & Biotech R&D</li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200">
            <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-4">
              <Beaker className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Analytical Grade Quality</h3>
            <p className="text-sm text-slate-600">
              All products conform to strict analytical-grade specifications with lot-specific 
              Technical Data Sheet and full product documentation.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200">
            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">48-Hour Review</h3>
            <p className="text-sm text-slate-600">
              All account applications are manually reviewed by our team to verify institutional 
              credentials. Approval typically takes 1-2 business days.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-slate-900 rounded-2xl p-8 text-center">
          <h2 className="font-heading font-bold text-2xl text-white mb-3">
            Ready to Access Our Reagent Catalog?
          </h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Apply with your institutional email (.edu, .org, .gov) to request access. 
            Our team will review your application within 48 hours.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="/register">
              <Button className="bg-white text-slate-900 hover:bg-slate-100 px-6 py-2.5 rounded-lg font-medium">
                Apply for Institutional Access
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="/login">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-6 py-2.5 rounded-lg font-medium">
                Sign In
              </Button>
            </a>
          </div>
        </div>

        {/* Research Use Disclaimer */}
        <div className="mt-12 p-6 bg-slate-100 border border-slate-200 rounded-xl text-center">
          <p className="text-sm font-medium text-slate-800 mb-2">FOR INSTITUTIONAL LABORATORY USE ONLY</p>
          <p className="text-sm text-slate-600">
            All products are sold strictly for institutional laboratory institutional laboratory use only. 
            Not for consumer, diagnostic, or therapeutic use. Not for diagnostic or institutional use. 
            Available to approved institutional accounts only.
          </p>
        </div>

        {/* Learn More Link */}
        <div className="mt-8 text-center">
          <a 
            href="/institutional-access" 
            className="text-sm text-slate-600 hover:text-slate-900 underline"
          >
            Learn more about our Institutional Access Policy
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-6">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Amidex. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="/privacy" className="hover:text-slate-900">Privacy Policy</a>
            <a href="/terms" className="hover:text-slate-900">Terms of Service</a>
            <a href="/institutional-access" className="hover:text-slate-900">Access Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthGate;
